import React, { useState } from 'react';
import styles from './ResultSection.module.scss';
import { IResultSection } from './ResultSection.interface';
import { Button } from '@zurich-primitives/button';
import { ReactComponent as DownloadIcon } from 'app/assets/Icons/DownloadWhite.svg';
import { ReactComponent as RepeatIcon } from 'app/assets/Icons/Repeat.svg';
import { Tooltip } from 'react-tooltip';

const truncateText = (text: string, maxLength: number): string =>
  text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;

const downloadBlob = (blob: Blob, targetName: string): void => {
  const safeName = targetName.trim() === '' ? 'download' : targetName.trim();
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = `${safeName}.xlsx`;
    document.body.appendChild(link);
    link.click();
  } finally {
    const appended = document.querySelector(`a[href="${url}"]`);
    appended?.remove();
    URL.revokeObjectURL(url);
  }
};

export const ResultSection: React.FC<IResultSection> = ({
  fileName,
  file,
  preview,
  totalRows,
  onNewClassification
}) => {
  const maxDescriptionLength = 50;
  const maxTypeOfLossLength = 40;
  const [fileNameState, setFileNameState] = useState<string>(
    fileName.replace(/\.xlsx?$/i, '')
  );

  const onFileNameChange = (newName: string): void => setFileNameState(newName);

  const downloadFile = (blob: Blob): void => downloadBlob(blob, fileNameState);

  const renderDownloadButton = (): React.JSX.Element => {
    return (
      <Button onClick={() => downloadFile(file)}>
        <Button.Icon>
          <DownloadIcon />
        </Button.Icon>
        <Button.Label>Download file</Button.Label>
      </Button>
    );
  };

  return (
    <div className={styles['result-section']}>
      <div className={styles.header}>
        <div className={styles['header-info']}>
          <h1 className={styles['section-title']}>Results</h1>
          <div className={styles['input-container']}>
            <span className={styles.label}>File Name</span>
            <span className={styles['file-name-preview']}>
              <input
                type="text"
                value={fileNameState}
                onChange={(e) => onFileNameChange(e.target.value)}
                className={styles['report-input']}
                placeholder={fileNameState}
              />
              <span className={styles.extension}>.xlsx</span>
            </span>
          </div>
        </div>

        <div className={styles['header-actions']}>
          <Button onClick={onNewClassification} variant="secondary">
            <Button.Icon>
              <RepeatIcon />
            </Button.Icon>
            <Button.Label>New classification</Button.Label>
          </Button>

          {renderDownloadButton()}
        </div>
      </div>

      <div className={styles.cards}>
        <div className={styles.card}>
          <p className={styles.label}>Rows processed</p>
          <p className={styles.value}>{totalRows}</p>
        </div>

        <div className={styles.card}>
          <p className={styles.label}>Discrepancies found by AI</p>
          <p>
            <i>Coming soon</i>
          </p>
        </div>
      </div>

      <div className={styles.preview}>
        <div className={styles['preview-header']}>
          <h3>Preview</h3>
          <p>
            20 out of {totalRows} rows displayed - download the result file to
            view all rows and columns.
          </p>
        </div>

        <div className={styles['table-container']}>
          <table className={styles['result-table']}>
            <thead>
              <tr>
                <th scope="col">Claim ID</th>
                <th scope="col">Claim Description</th>
                <th scope="col">Type of Loss</th>
                <th scope="col">Confidence</th>
              </tr>
            </thead>
            <tbody>
              {preview.map((item) => (
                <tr key={item.claim_id}>
                  <td>{item.claim_id}</td>

                  <td>
                    <span
                      data-tooltip-id={`description-${item.claim_id}`}
                      data-tooltip-content={
                        item.claim_description.length > maxDescriptionLength
                          ? item.claim_description
                          : undefined
                      }
                    >
                      {truncateText(
                        item.claim_description,
                        maxDescriptionLength
                      )}
                    </span>
                    {item.claim_description.length > maxDescriptionLength && (
                      <Tooltip
                        id={`description-${item.claim_id}`}
                        className={styles.tooltip}
                      />
                    )}
                  </td>

                  <td>
                    <span
                      data-tooltip-id={`tol-${item.claim_id}`}
                      data-tooltip-content={
                        item.type_of_loss_ai.length > maxTypeOfLossLength
                          ? item.type_of_loss_ai
                          : undefined
                      }
                    >
                      {truncateText(item.type_of_loss_ai, maxTypeOfLossLength)}
                    </span>
                    {item.type_of_loss_ai.length > maxTypeOfLossLength && (
                      <Tooltip
                        id={`tol-${item.claim_id}`}
                        className={styles.tooltip}
                      />
                    )}
                  </td>

                  <td>{item.confidence_percentage}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
